import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Toast from "../Toast/Toast";
import { apiFetch, readJsonSafe } from "../config/apiClient";
import { clearAuth, getUser } from "../config/auth";
import "./AdminUsersPage.css";

const PAGE_SIZE = 100;

function SubscriptionPlanBadge({ plan }) {
  const normalizedPlan = String(plan || "FREE").trim().toUpperCase() || "FREE";
  const knownPlans = new Set(["FREE", "BASIC", "PREMIUM", "PRO"]);
  const tone = knownPlans.has(normalizedPlan) ? normalizedPlan.toLowerCase() : "unknown";

  return (
    <span className={`admin-users-plan-badge ${tone}`}>
      {normalizedPlan}
    </span>
  );
}

function UserStatusBadge({ blocked }) {
  return (
    <span className={`admin-users-status ${blocked ? "blocked" : "active"}`}>
      {blocked ? "Blocked" : "Active"}
    </span>
  );
}

function BlockUnblockButton({ blocked, busy, disabled, onClick, label }) {
  return (
    <button
      type="button"
      className={`admin-users-action-btn ${blocked ? "unblock" : "block"}`}
      disabled={busy || disabled}
      onClick={onClick}
    >
      {busy ? "Saving..." : label || (blocked ? "Unblock" : "Block")}
    </button>
  );
}

function PaginationControls({
  page,
  totalPages,
  totalElements,
  last,
  loading,
  onPrevious,
  onNext,
}) {
  return (
    <div className="admin-users-pagination">
      <p className="admin-users-pagination-copy">
        Page {totalPages ? page + 1 : 0} of {totalPages || 0}
      </p>
      <p className="admin-users-page-indicator">{totalElements} total users</p>
      <div className="admin-users-pagination-actions">
        <button
          type="button"
          className="admin-users-page-btn"
          onClick={onPrevious}
          disabled={loading || page === 0}
        >
          Previous
        </button>
        <button
          type="button"
          className="admin-users-page-btn"
          onClick={onNext}
          disabled={loading || last || totalPages <= 1}
        >
          Next
        </button>
      </div>
    </div>
  );
}

function UsersTable({ users, actionLoadingUserId, currentAdminId, onToggleStatus }) {
  if (!users.length) {
    return (
      <div className="admin-users-empty">
        <p>No users found on this page.</p>
      </div>
    );
  }

  return (
    <div className="admin-users-table-wrap">
      <table className="admin-users-table">
        <thead>
          <tr>
            <th>ID</th>
            <th>Username</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Role</th>
            <th>Subscription Plan</th>
            <th>Status</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user) => {
            const isCurrentAdmin = String(user.id) === String(currentAdminId);
            const actionDisabled = isCurrentAdmin && !user.blocked;

            return (
              <tr key={user.id} className={user.blocked ? "is-blocked" : ""}>
                <td data-label="ID">
                  {user.id}
                  {isCurrentAdmin ? (
                    <span className="admin-users-current-tag">You</span>
                  ) : null}
                </td>
                <td data-label="Username">{user.username || "-"}</td>
                <td data-label="Email">{user.emailId || "-"}</td>
                <td data-label="Phone">{user.phone || "-"}</td>
                <td data-label="Role">{user.role || "STUDENT"}</td>
                <td data-label="Subscription Plan">
                  <SubscriptionPlanBadge plan={user.subscriptionPlan} />
                </td>
                <td data-label="Status">
                  <UserStatusBadge blocked={user.blocked} />
                </td>
                <td data-label="Action">
                  <BlockUnblockButton
                    blocked={user.blocked}
                    busy={actionLoadingUserId === user.id}
                    disabled={actionDisabled}
                    label={actionDisabled ? "Current Admin" : undefined}
                    onClick={() => onToggleStatus(user)}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function AdminUsersPage() {
  const navigate = useNavigate();
  const currentAdminId = getUser()?.id ?? null;

  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [last, setLast] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [unauthorized, setUnauthorized] = useState(false);
  const [actionLoadingUserId, setActionLoadingUserId] = useState(null);
  const [pendingStatusUser, setPendingStatusUser] = useState(null);

  useEffect(() => {
    const parsedUser = getUser();
    if (!parsedUser) {
      clearAuth();
      navigate("/login", { replace: true });
      return;
    }

    if (String(parsedUser?.role || "STUDENT").toUpperCase() !== "ADMIN") {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const loadUsers = useCallback(
    async (pageIndex) => {
      setLoading(true);
      setError("");
      setUnauthorized(false);

      try {
        const response = await apiFetch(
          `/users/registered/paged?page=${pageIndex}&size=${PAGE_SIZE}`,
          { auth: true }
        );
        const data = await readJsonSafe(response);

        if (response.status === 401) {
          clearAuth();
          navigate("/login", { replace: true });
          return;
        }

        if (response.status === 403) {
          setUnauthorized(true);
          setError("Unauthorized");
          return;
        }

        if (!response.ok) {
          throw new Error(data?.message || "Failed to load registered users.");
        }

        setUsers(Array.isArray(data?.content) ? data.content : []);
        setPage(Number(data?.page) || 0);
        setTotalPages(Math.max(0, Number(data?.totalPages) || 0));
        setTotalElements(Math.max(0, Number(data?.totalElements) || 0));
        setLast(Boolean(data?.last));
      } catch (err) {
        setError(err.message || "Failed to load registered users.");
      } finally {
        setLoading(false);
      }
    },
    [navigate]
  );

  useEffect(() => {
    loadUsers(0);
  }, [loadUsers]);

  const handleToggleStatus = (user) => {
    if (String(user?.id) === String(currentAdminId) && !user?.blocked) {
      setError("You cannot block the currently logged-in admin.");
      return;
    }
    setPendingStatusUser(user);
  };

  const confirmToggleStatus = async () => {
    if (!pendingStatusUser) return;
    const user = pendingStatusUser;

    setActionLoadingUserId(user.id);
    setError("");
    setPendingStatusUser(null);

    try {
      const response = await apiFetch(
        `/users/${user.id}/${user.blocked ? "unblock" : "block"}`,
        {
          method: "PUT",
          auth: true,
        }
      );
      const data = await readJsonSafe(response);

      if (response.status === 401) {
        clearAuth();
        navigate("/login", { replace: true });
        return;
      }

      if (response.status === 403) {
        setUnauthorized(true);
        setError("Unauthorized");
        return;
      }

      if (!response.ok) {
        throw new Error(
          data?.message ||
            `Failed to ${user.blocked ? "unblock" : "block"} user.`
        );
      }

      setUsers((prev) =>
        prev.map((entry) =>
          entry.id === user.id ? { ...entry, blocked: !user.blocked } : entry
        )
      );
      setToastMessage(
        data?.message ||
          `User ${user.blocked ? "unblocked" : "blocked"} successfully.`
      );
    } catch (err) {
      setError(
        err.message ||
          `Failed to ${user.blocked ? "unblock" : "block"} user.`
      );
    } finally {
        setActionLoadingUserId(null);
    }
  };

  return (
    <div className="admin-users-page">
      <div className="admin-users-header">
        <div>
          <p className="admin-users-eyebrow">Admin Panel</p>
          <h1>Users Management</h1>
          <p className="admin-users-copy">
            View registered users in server pages of 100 and manage block or
            unblock access.
          </p>
        </div>
        <div className="admin-users-toolbar">
          <button
            type="button"
            className="admin-users-nav-btn"
            onClick={() => navigate("/admin/dashboard")}
          >
            Back To Admin
          </button>
          <button
            type="button"
            className="admin-users-nav-btn primary"
            onClick={() => loadUsers(page)}
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh"}
          </button>
        </div>
      </div>

      {error ? <p className="admin-users-error">{error}</p> : null}
      {unauthorized ? (
        <div className="admin-users-empty unauthorized">
          <p>You are not authorized to manage users.</p>
        </div>
      ) : null}

      {!unauthorized && loading ? (
        <div className="admin-users-loading">Loading users page...</div>
      ) : null}

      {!unauthorized && !loading ? (
        <>
          <UsersTable
            users={users}
            actionLoadingUserId={actionLoadingUserId}
            currentAdminId={currentAdminId}
            onToggleStatus={handleToggleStatus}
          />
          <PaginationControls
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            last={last}
            loading={loading}
            onPrevious={() => loadUsers(Math.max(0, page - 1))}
            onNext={() => loadUsers(page + 1)}
          />
        </>
      ) : null}

      <Toast
        message={toastMessage}
        onClose={() => setToastMessage("")}
        duration={2500}
      />

      {pendingStatusUser ? (
        <div className="admin-confirm-overlay" role="dialog" aria-modal="true" aria-labelledby="admin-user-confirm-title">
          <div className="admin-confirm-card">
            <p className="admin-confirm-eyebrow">Confirm Action</p>
            <h2 id="admin-user-confirm-title">
              {pendingStatusUser.blocked ? "Unblock this user?" : "Block this user?"}
            </h2>
            <p className="admin-confirm-copy">
              {pendingStatusUser.blocked
                ? "This user will regain access to protected areas of the app."
                : "This user will be prevented from using protected areas of the app."}
            </p>
            <div className="admin-confirm-actions">
              <button
                type="button"
                className="admin-users-nav-btn"
                onClick={() => setPendingStatusUser(null)}
                disabled={actionLoadingUserId === pendingStatusUser.id}
              >
                Cancel
              </button>
              <button
                type="button"
                className="admin-users-nav-btn primary"
                onClick={confirmToggleStatus}
                disabled={actionLoadingUserId === pendingStatusUser.id}
              >
                {actionLoadingUserId === pendingStatusUser.id ? "Saving..." : pendingStatusUser.blocked ? "Unblock User" : "Block User"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AdminUsersPage;
